import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { SkillGuidanceEntry, SkillGuidanceKey, SkillGuidanceMap } from "@/api/admin-api"

import { Copy, FileText, Plus, Save, Trash2 } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { useRegisterAdminDirty } from "@/features/admin-dashboard/admin-unsaved-changes-context"

import { RawJsonTab } from "./shared/raw-json-tab"
import {
  useReplaceSkillEnabledMutation,
  useReplaceSkillGuidanceMutation,
  useSkillEnabledQuery,
  useSkillGuidanceQuery,
} from "../hooks/admin-queries"

const SKILLS: Array<{ id: SkillGuidanceKey; label: string }> = [
  { id: "docx", label: "DOCX" },
  { id: "pdf", label: "PDF" },
  { id: "pptx", label: "PPTX" },
  { id: "xlsx", label: "XLSX" },
]

const DEFAULT_GUIDANCE: SkillGuidanceMap = {
  docx: {
    "visual-quality": {
      id: "visual-quality",
      title: "Visual quality",
      enabled: false,
      autoload: true,
      content:
        "Before delivering a DOCX, render pages and revise typography, spacing, section hierarchy, headers, footers, and page breaks until the document looks intentionally designed.",
    },
  },
  pdf: {
    "visual-quality": {
      id: "visual-quality",
      title: "Visual quality",
      enabled: false,
      autoload: true,
      content:
        "For generated PDFs, render the output and check page flow, margins, image resolution, line breaks, and text contrast before claiming the artifact is ready.",
    },
  },
  pptx: {
    "creative-range": {
      id: "creative-range",
      title: "Creative range",
      enabled: false,
      autoload: false,
      content:
        "When building decks, use the static PPTX instructions as technical guardrails while still choosing narrative structure, layouts, imagery, and visual rhythm appropriate to the user's goal.",
    },
  },
  xlsx: {
    "workbook-quality": {
      id: "workbook-quality",
      title: "Workbook quality",
      enabled: false,
      autoload: true,
      content:
        "Prefer polished workbook models: clear sheet names, frozen headers, number formats, formulas where useful, validation checks, and readable charts instead of raw data dumps.",
    },
  },
}

function cloneGuidance(guidance: SkillGuidanceMap | undefined): SkillGuidanceMap {
  const source = guidance && Object.keys(guidance).length > 0 ? guidance : DEFAULT_GUIDANCE
  return JSON.parse(JSON.stringify(source)) as SkillGuidanceMap
}

function createNextId(entries: Record<string, SkillGuidanceEntry>, base = "guidance"): string {
  let index = 1
  let candidate = base
  while (Object.prototype.hasOwnProperty.call(entries, candidate)) {
    candidate = `${base}-${index}`
    index += 1
  }
  return candidate
}

function isDirty(current: SkillGuidanceMap, committed: SkillGuidanceMap): boolean {
  return JSON.stringify(current) !== JSON.stringify(committed)
}

export function SkillGuidancePanel() {
  const { t } = useTranslation()
  const { data, isLoading } = useSkillGuidanceQuery()
  const { data: enabledData } = useSkillEnabledQuery()
  const replaceMutation = useReplaceSkillGuidanceMutation()
  const replaceEnabledMutation = useReplaceSkillEnabledMutation()
  const committedGuidance = useMemo(() => cloneGuidance(data?.guidance), [data?.guidance])
  const [draft, setDraft] = useState<SkillGuidanceMap>(committedGuidance)
  const [selectedSkill, setSelectedSkill] = useState<SkillGuidanceKey>("docx")
  const [resetSignal, setResetSignal] = useState(0)

  // A skill is enabled unless an admin explicitly set its flag to false.
  const skillEnabled = enabledData?.enabled[selectedSkill] !== false

  const handleToggleSkillEnabled = async (checked: boolean) => {
    if (!enabledData?.hash) {
      toast.error(
        t("adminDashboard.systemConfig.skillEnabledMissingHash", {
          defaultValue: "Missing hash — reload the page and try again",
        })
      )
      return
    }
    const nextEnabled = { ...enabledData.enabled, [selectedSkill]: checked }
    try {
      await replaceEnabledMutation.mutateAsync({
        enabled: nextEnabled,
        expectedHash: enabledData.hash,
        reason: `${checked ? "Enabled" : "Disabled"} the ${selectedSkill} skill from the admin dashboard`,
      })
      toast.success(
        checked
          ? t("adminDashboard.systemConfig.skillEnabledOn", {
              defaultValue: "Skill enabled",
            })
          : t("adminDashboard.systemConfig.skillEnabledOff", {
              defaultValue: "Skill disabled",
            })
      )
    } catch (err) {
      toast.error(
        t("adminDashboard.systemConfig.skillEnabledSaveFailed", {
          defaultValue: "Failed to update skill availability",
        }),
        { description: err instanceof Error ? err.message : undefined }
      )
    }
  }

  useEffect(() => {
    setDraft(committedGuidance)
    setResetSignal((value) => value + 1)
  }, [committedGuidance])

  const entries = draft[selectedSkill] ?? {}
  const dirty = isDirty(draft, committedGuidance)
  useRegisterAdminDirty("skill-guidance", dirty)

  const setEntries = (nextEntries: Record<string, SkillGuidanceEntry>) => {
    setDraft((current) => ({ ...current, [selectedSkill]: nextEntries }))
  }

  const updateEntry = (entryId: string, updates: Partial<SkillGuidanceEntry>) => {
    const current = entries[entryId]
    setEntries({ ...entries, [entryId]: { ...current, ...updates } })
  }

  const handleAdd = () => {
    const id = createNextId(entries)
    setEntries({
      ...entries,
      [id]: {
        id,
        title: "New guidance",
        enabled: false,
        autoload: true,
        content: "",
      },
    })
  }

  const handleDuplicate = (entry: SkillGuidanceEntry) => {
    const id = createNextId(entries, entry.id)
    setEntries({
      ...entries,
      [id]: {
        ...entry,
        id,
        title: `${entry.title} copy`,
        enabled: false,
      },
    })
  }

  const handleDelete = (entryId: string) => {
    const nextEntries = { ...entries }
    delete nextEntries[entryId]
    setEntries(nextEntries)
  }

  const handleSave = async (nextGuidance: SkillGuidanceMap, reason: string, source = "form") => {
    if (!data?.hash) {
      throw new Error(
        t("adminDashboard.systemConfig.skillGuidanceMissingHash", {
          defaultValue: "Missing hash — reload the page and try again",
        })
      )
    }
    await replaceMutation.mutateAsync({
      guidance: nextGuidance,
      expectedHash: data.hash,
      reason,
      source,
    })
    toast.success(
      t("adminDashboard.systemConfig.skillGuidanceSaved", {
        defaultValue: "Skill guidance saved",
      })
    )
  }

  const handleFormSave = async () => {
    try {
      await handleSave(
        draft,
        t("adminDashboard.systemConfig.skillGuidanceFormReason", {
          defaultValue: "Updated skill guidance from the form editor",
        })
      )
    } catch (err) {
      toast.error(
        t("adminDashboard.systemConfig.skillGuidanceSaveFailed", {
          defaultValue: "Failed to save skill guidance",
        }),
        { description: err instanceof Error ? err.message : undefined }
      )
    }
  }

  const handleRawSave = async (next: unknown, reason: string) => {
    try {
      await handleSave(next as SkillGuidanceMap, reason, "raw")
      setResetSignal((value) => value + 1)
    } catch (err) {
      toast.error(
        t("adminDashboard.systemConfig.skillGuidanceRawSaveFailed", {
          defaultValue: "Raw save failed",
        }),
        { description: err instanceof Error ? err.message : undefined }
      )
      throw err
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10" />
        <Skeleton className="h-28" />
      </div>
    )
  }

  return (
    <Tabs data-admin-dirty={dirty ? "true" : undefined} defaultValue="form">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <TabsList>
          <TabsTrigger value="form">
            {t("adminDashboard.systemConfig.skillGuidanceFormTab", { defaultValue: "Form" })}
          </TabsTrigger>
          <TabsTrigger value="raw">
            {t("adminDashboard.systemConfig.skillGuidanceRawTab", { defaultValue: "Raw JSON" })}
          </TabsTrigger>
        </TabsList>
        <Button
          disabled={!dirty || replaceMutation.isPending}
          type="button"
          onClick={handleFormSave}
        >
          <Save className="size-4" />
          {t("adminDashboard.systemConfig.skillGuidanceSave", { defaultValue: "Save" })}
        </Button>
      </div>

      <TabsContent value="form">
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <Select
                value={selectedSkill}
                onValueChange={(value) => setSelectedSkill(value as SkillGuidanceKey)}
              >
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SKILLS.map((skill) => (
                    <SelectItem key={skill.id} value={skill.id}>
                      {skill.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <Switch
                  checked={skillEnabled}
                  disabled={replaceEnabledMutation.isPending}
                  id="skill-enabled-toggle"
                  aria-label={t("adminDashboard.systemConfig.skillEnabledLabel", {
                    defaultValue: "Skill enabled",
                  })}
                  onCheckedChange={handleToggleSkillEnabled}
                />
                <Label className="text-sm" htmlFor="skill-enabled-toggle">
                  {skillEnabled
                    ? t("adminDashboard.systemConfig.skillEnabledLabel", {
                        defaultValue: "Skill enabled",
                      })
                    : t("adminDashboard.systemConfig.skillDisabledLabel", {
                        defaultValue: "Skill disabled",
                      })}
                </Label>
              </div>
            </div>
            <Button type="button" variant="outline" onClick={handleAdd}>
              <Plus className="size-4" />
              {t("adminDashboard.systemConfig.skillGuidanceAdd", { defaultValue: "Add" })}
            </Button>
          </div>
          {!skillEnabled && (
            <div className="border-destructive/40 bg-destructive/10 text-destructive flex items-center gap-2 rounded-md border p-3 text-sm">
              <FileText className="size-4" />
              {t("adminDashboard.systemConfig.skillDisabledNotice", {
                defaultValue:
                  "This skill is disabled — the assistant cannot load it and its code-interpreter bundle is not attached. Guidance below is stored but inactive until re-enabled.",
              })}
            </div>
          )}

          <div className="space-y-3">
            {Object.values(entries).length === 0 ? (
              <div className="border-border text-muted-foreground flex items-center gap-2 rounded-md border p-4 text-sm">
                <FileText className="size-4" />
                {t("adminDashboard.systemConfig.skillGuidanceEmpty", {
                  defaultValue: "No guidance entries for this skill.",
                })}
              </div>
            ) : (
              Object.values(entries).map((entry) => (
                <div key={entry.id} className="border-border rounded-md border p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h5 className="text-sm font-semibold">{entry.title}</h5>
                        <Badge variant={entry.enabled ? "default" : "secondary"}>{entry.id}</Badge>
                      </div>
                      <p className="text-muted-foreground text-xs">
                        {entry.autoload
                          ? t("adminDashboard.systemConfig.skillGuidanceAutoloadOn", {
                              defaultValue: "Autoloads when this skill is loaded",
                            })
                          : t("adminDashboard.systemConfig.skillGuidanceAutoloadOff", {
                              defaultValue: "Stored only; not injected automatically",
                            })}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="icon"
                        type="button"
                        variant="ghost"
                        onClick={() => handleDuplicate(entry)}
                      >
                        <Copy className="size-4" />
                      </Button>
                      <Button
                        size="icon"
                        type="button"
                        variant="ghost"
                        onClick={() => handleDelete(entry.id)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(180px,260px)_1fr]">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor={`skill-guidance-title-${entry.id}`}>
                          {t("adminDashboard.systemConfig.skillGuidanceTitle", {
                            defaultValue: "Title",
                          })}
                        </Label>
                        <Input
                          id={`skill-guidance-title-${entry.id}`}
                          value={entry.title}
                          onChange={(event) => updateEntry(entry.id, { title: event.target.value })}
                        />
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <Label htmlFor={`skill-guidance-enabled-${entry.id}`}>
                          {t("adminDashboard.systemConfig.skillGuidanceEnabled", {
                            defaultValue: "Enabled",
                          })}
                        </Label>
                        <Switch
                          checked={entry.enabled}
                          id={`skill-guidance-enabled-${entry.id}`}
                          onCheckedChange={(checked) => updateEntry(entry.id, { enabled: checked })}
                        />
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <Label htmlFor={`skill-guidance-autoload-${entry.id}`}>
                          {t("adminDashboard.systemConfig.skillGuidanceAutoload", {
                            defaultValue: "Autoload",
                          })}
                        </Label>
                        <Switch
                          checked={entry.autoload}
                          id={`skill-guidance-autoload-${entry.id}`}
                          onCheckedChange={(checked) =>
                            updateEntry(entry.id, { autoload: checked })
                          }
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`skill-guidance-content-${entry.id}`}>
                        {t("adminDashboard.systemConfig.skillGuidanceContent", {
                          defaultValue: "Markdown guidance",
                        })}
                      </Label>
                      <Textarea
                        className="min-h-40 font-mono text-sm"
                        id={`skill-guidance-content-${entry.id}`}
                        value={entry.content}
                        onChange={(event) => updateEntry(entry.id, { content: event.target.value })}
                      />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </TabsContent>

      <TabsContent value="raw">
        <RawJsonTab
          confirmToken="skill-guidance"
          isPending={replaceMutation.isPending}
          resetSignal={resetSignal}
          value={committedGuidance}
          resourceLabel={t("adminDashboard.systemConfig.skillGuidanceRawLabel", {
            defaultValue: "skill guidance",
          })}
          onSave={handleRawSave}
        />
      </TabsContent>
    </Tabs>
  )
}
